import React from 'react';
import { cn } from '@/lib/utils';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  message?: string;
  className?: string;
  showMessage?: boolean;
  fullHeight?: boolean;
}

/**
 * Minimalist loading spinner for Admin Portal
 * Features a clean dot-based animation without borders
 */
const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  message = 'Loading data...',
  className = '',
  showMessage = true,
  fullHeight = false
}) => {
  // Size variants for the dots
  const dotSizeClasses = {
    sm: 'w-1.5 h-1.5',
    md: 'w-2 h-2',
    lg: 'w-2.5 h-2.5',
    xl: 'w-3 h-3'
  };

  // Gap between dots
  const gapClasses = {
    sm: 'gap-1',
    md: 'gap-1.5',
    lg: 'gap-2',
    xl: 'gap-2.5'
  };

  // Container height variants
  const containerClasses = fullHeight
    ? 'flex items-center justify-center min-h-screen'
    : 'flex items-center justify-center py-8';

  return (
    <div className={cn(containerClasses, className)}>
      <div className="text-center">
        {/* Minimalist three-dot spinner */}
        <div className={cn('flex items-center justify-center', gapClasses[size], showMessage ? 'mb-3' : '')}>
          <div className={cn(
            'rounded-full bg-blue-600 loading-dot',
            dotSizeClasses[size]
          )}></div>
          <div className={cn(
            'rounded-full bg-blue-600 loading-dot',
            dotSizeClasses[size]
          )}></div>
          <div className={cn(
            'rounded-full bg-blue-600 loading-dot',
            dotSizeClasses[size]
          )}></div>
        </div>

        {showMessage && (
          <p className="text-muted-foreground text-sm">{message}</p>
        )}
      </div>
    </div>
  );
};

interface InlineSpinnerProps {
  size?: 'xs' | 'sm' | 'md';
  className?: string;
}

/**
 * Inline loading spinner for buttons and small spaces
 * Minimalist dot design without borders
 */
export const InlineSpinner: React.FC<InlineSpinnerProps> = ({ size = 'sm', className = '' }) => {
  const dotSizeClasses = {
    xs: 'w-1 h-1',
    sm: 'w-1.5 h-1.5',
    md: 'w-2 h-2'
  };

  const gapClasses = {
    xs: 'gap-0.5',
    sm: 'gap-1',
    md: 'gap-1'
  };

  return (
    <div className={cn('flex items-center justify-center', gapClasses[size], className)}>
      <div className={cn(
        'rounded-full bg-blue-600 loading-dot',
        dotSizeClasses[size]
      )}></div>
      <div className={cn(
        'rounded-full bg-blue-600 loading-dot',
        dotSizeClasses[size]
      )}></div>
      <div className={cn(
        'rounded-full bg-blue-600 loading-dot',
        dotSizeClasses[size]
      )}></div>
    </div>
  );
};

interface TableLoadingSpinnerProps {
  message?: string;
  colSpan?: number;
}

/**
 * Table loading spinner - specifically for table content areas
 * Minimalist dot design without borders
 */
export const TableLoadingSpinner: React.FC<TableLoadingSpinnerProps> = ({
  message = 'Loading table data...',
  colSpan = 1
}) => {
  return (
    <tr>
      <td colSpan={colSpan} className="px-6 py-8 text-center">
        <div className="text-center">
          <div className="flex items-center justify-center gap-1.5 mx-auto mb-3">
            <div className="w-2 h-2 rounded-full bg-blue-600 loading-dot"></div>
            <div className="w-2 h-2 rounded-full bg-blue-600 loading-dot"></div>
            <div className="w-2 h-2 rounded-full bg-blue-600 loading-dot"></div>
          </div>
          <p className="text-muted-foreground text-sm">{message}</p>
        </div>
      </td>
    </tr>
  );
};

interface PageLoadingSpinnerProps {
  message?: string;
}

/**
 * Page loading spinner - for full page loads
 */
export const PageLoadingSpinner: React.FC<PageLoadingSpinnerProps> = ({
  message = 'Loading page content...'
}) => {
  return (
    <LoadingSpinner
      size="lg"
      message={message}
      fullHeight={true}
    />
  );
};

interface CardLoadingSpinnerProps {
  message?: string;
}

/**
 * Card loading spinner - for card content areas
 * Minimalist dot design without borders
 */
export const CardLoadingSpinner: React.FC<CardLoadingSpinnerProps> = ({
  message = 'Loading content...'
}) => {
  return (
    <div className="flex items-center justify-center py-8">
      <div className="text-center">
        <div className="flex items-center justify-center gap-1.5 mx-auto mb-3">
          <div className="w-2 h-2 rounded-full bg-blue-600 loading-dot"></div>
          <div className="w-2 h-2 rounded-full bg-blue-600 loading-dot"></div>
          <div className="w-2 h-2 rounded-full bg-blue-600 loading-dot"></div>
        </div>
        <p className="text-muted-foreground text-sm">{message}</p>
      </div>
    </div>
  );
};

export default LoadingSpinner;
